import { useErrorReporter } from '@/composables/useErrorReporter'
import {
	ACCOUNT_URL, API_BASE, PORTAL_URL
} from '@/constants/index.js'
import type { IntercomUserData } from '@/types/intercom'
import {
	computed, ref
} from 'vue'

const loggedIn = ref(false)
const isInternal = ref(false)
const accountUrl = ref(ACCOUNT_URL)
const loading = ref(true)

const accountId = ref('')
const accountName = ref('')
const accountEmail = ref('')
const accountCreatedTime = ref('')
const accountPhone = ref('')

const intercomUserId = ref('')

const intercomSettings = computed(() => {
	const userData: IntercomUserData = {
	}
	if (accountEmail.value) {
		userData.email = accountEmail.value
	}
	if (intercomUserId.value) {
		userData.user_id = intercomUserId.value.toString()
	}
	if (accountName.value) {
		userData.name = accountName.value
	}
	if (accountCreatedTime.value) {
		userData.created_at = accountCreatedTime.value
	}
	if (accountPhone.value) {
		userData.phone = accountPhone.value
	}

	userData.hide_default_launcher = true

	return userData
})

interface AuthResponse {
	emailId: string
	siteAdmin: boolean
	accounts: {
		accountId: string
		intercomUserId: string
		accountCreatedTime: string
	}[]
	contact: {
		first: string
		last: string
		email: string
		phone: string
	}

}

export function useAuth() {
	const { bugsnagReport } = useErrorReporter()

	async function getAuth() {
		loading.value = true

		let authResponse: AuthResponse | null = null

		await $fetch<AuthResponse>(`${API_BASE}/auth`, {
			method: 'GET',
			credentials: 'include',
			onResponse({ response }) {
				if (!response.ok) {
					const error = response._data?.errors?.[0] || response._data
					throw new Error(error.title || 'Failed to get authentication status')
				}

				authResponse = response._data

				if (!authResponse) {
					loggedIn.value = false
					isInternal.value = false
					accountUrl.value = ACCOUNT_URL
					return
				}

				loggedIn.value = !!authResponse.emailId
				isInternal.value = !!authResponse.siteAdmin
				accountUrl.value = isInternal.value ? PORTAL_URL : ACCOUNT_URL

				// Populate user data
				accountId.value = authResponse?.accounts?.[0]?.accountId || ''
				accountName.value = `${authResponse?.contact?.first || ''} ${authResponse?.contact?.last || ''}`
				accountEmail.value = authResponse?.contact?.email || ''
				accountCreatedTime.value = authResponse?.accounts?.[0]?.accountCreatedTime || ''
				accountPhone.value = authResponse?.contact?.phone || ''
				intercomUserId.value = authResponse?.accounts?.[0]?.intercomUserId || accountId.value || ''
			}
		}).catch(error => {
			bugsnagReport({
				error: new Error(`Authentication check failed: ${error.message}`),
				showToast: false, // Don't show toast for auth checks as they happen silently
				metaData: {
					endpoint: `${API_BASE}/auth`,
					context: 'authentication_check'
				}
			})

			// Set fallback state
			loggedIn.value = false
			isInternal.value = false
			accountUrl.value = ACCOUNT_URL
			authResponse = null
		}).finally(() => {
			loading.value = false
		})

		if (!authResponse) {
			return null
		}
	}
	return {
		getAuth,
		loggedIn,
		isInternal,
		intercomSettings,
		loading,
		accountId,
		accountUrl,
		accountName,
		accountEmail,
		accountCreatedTime,
		accountPhone
	}
}
